import React, { useState, useMemo } from 'react';
import { theme } from '../../styles/theme';
import { PassiveTemplate, PassiveBucket } from '../../data/passives';

const BUCKET_COLORS: Record<PassiveBucket, string> = {
  training:    '#66ccff',
  background:  '#aa88ff',
  contacts:    '#ffaa44',
  loadout:     '#44cc88',
  perception:  '#ff5ac4',
  economy:     '#ffcc44',
  combat:      '#ff5555',
};

interface PassiveDraftScreenProps {
  options: PassiveTemplate[];
  ownedPassives: PassiveTemplate[];
  onPick: (key: string) => void;
  /** True on the initial run-start draft, false on per-round drafts after kills. */
  runStart: boolean;
  /** Current round number — shown on per-round drafts. */
  round?: number;
}

export function PassiveDraftScreen({
  options,
  ownedPassives,
  onPick,
  runStart,
  round,
}: PassiveDraftScreenProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const headerLabel = useMemo(() => {
    if (runStart) return 'OPERATIVE DOSSIER';
    return `ROUND ${round ?? '?'} — INTEL UPGRADE`;
  }, [runStart, round]);

  const subtitleText = runStart
    ? 'Select a background trait to define this operative. This passive persists for the entire run.'
    : 'Kill confirmed. Select a trait to carry into the next round.';

  const handleConfirm = () => {
    if (!selectedKey || confirmed) return;
    setConfirmed(true);
    onPick(selectedKey);
  };

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.classification}>CLASSIFIED // OPERATIVE TRAITS</div>
        <div style={styles.title}>{headerLabel}</div>
        <div style={styles.subtitle}>{subtitleText}</div>

        <div style={styles.cardGrid}>
          {options.map((passive) => {
            const color = BUCKET_COLORS[passive.bucket];
            const isSelected = selectedKey === passive.key;
            return (
              <button
                key={passive.key}
                style={{
                  ...styles.cardButton,
                  ...(isSelected ? {
                    borderColor: color,
                    background: `${color}10`,
                  } : {}),
                }}
                onClick={() => !confirmed && setSelectedKey(passive.key)}
                disabled={confirmed}
                onMouseEnter={e => {
                  if (!confirmed && !isSelected) {
                    (e.currentTarget as HTMLElement).style.borderColor = `${color}80`;
                    (e.currentTarget as HTMLElement).style.background = `${color}08`;
                  }
                }}
                onMouseLeave={e => {
                  if (!confirmed && !isSelected) {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.1)';
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
                  }
                }}
              >
                <div style={{ ...styles.cardBucket, color }}>
                  {passive.bucket.toUpperCase()}
                </div>
                <div style={styles.cardName}>{passive.name}</div>
                <div style={styles.cardDesc}>{passive.description}</div>
                {isSelected && (
                  <div style={{ ...styles.selectedBadge, background: color }}>
                    SELECTED
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div style={styles.actionRow}>
          <button
            style={{
              ...styles.confirmButton,
              opacity: selectedKey ? 1 : 0.3,
              cursor: selectedKey ? 'pointer' : 'default',
            }}
            onClick={handleConfirm}
            disabled={!selectedKey || confirmed}
          >
            CONFIRM
          </button>
        </div>

        {ownedPassives.length > 0 && (
          <div style={styles.ownedSection}>
            <div style={styles.ownedLabel}>OPERATIVE TRAITS</div>
            <div style={styles.ownedList}>
              {ownedPassives.map((p) => {
                const color = BUCKET_COLORS[p.bucket];
                return (
                  <div key={p.key} style={{
                    ...styles.ownedChip,
                    borderColor: `${color}60`,
                    color,
                  }}>
                    {p.name}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.fonts.mono,
    overflow: 'auto',
  },
  inner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '780px',
    width: '100%',
    padding: '40px 24px',
  },
  classification: {
    fontSize: '10px',
    letterSpacing: '4px',
    color: '#ff5050',
    fontWeight: 700,
    marginBottom: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    letterSpacing: '4px',
    color: theme.colors.textPrimary,
    marginBottom: '8px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '12px',
    color: theme.colors.textSecondary,
    letterSpacing: '0.5px',
    marginBottom: '32px',
    textAlign: 'center',
    lineHeight: '1.6',
    maxWidth: '520px',
  },
  cardGrid: {
    display: 'flex',
    gap: '16px',
    width: '100%',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '28px',
  },
  cardButton: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    padding: '20px 16px',
    width: '220px',
    minHeight: '190px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    textAlign: 'left',
    position: 'relative',
    transition: 'all 0.15s',
    display: 'flex',
    flexDirection: 'column',
  },
  cardBucket: {
    fontSize: '9px',
    letterSpacing: '2px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  cardName: {
    fontSize: '14px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    marginBottom: '10px',
    letterSpacing: '0.5px',
  },
  cardDesc: {
    fontSize: '10px',
    color: theme.colors.textSecondary,
    lineHeight: '1.5',
  },
  selectedBadge: {
    position: 'absolute',
    top: '-10px',
    right: '10px',
    fontSize: '9px',
    letterSpacing: '2px',
    fontWeight: 700,
    color: '#000',
    padding: '4px 8px',
    borderRadius: '3px',
  },
  actionRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
  },
  confirmButton: {
    background: 'rgba(68, 204, 136, 0.06)',
    border: '1px solid rgba(68, 204, 136, 0.5)',
    color: '#44cc88',
    padding: '12px 32px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '4px',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
    transition: 'all 0.15s',
  },
  ownedSection: {
    marginTop: '24px',
    width: '100%',
    maxWidth: '600px',
    textAlign: 'center',
  },
  ownedLabel: {
    fontSize: '9px',
    letterSpacing: '3px',
    color: theme.colors.textSecondary,
    fontWeight: 700,
    marginBottom: '10px',
  },
  ownedList: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '8px',
  },
  ownedChip: {
    fontSize: '10px',
    letterSpacing: '1px',
    fontWeight: 600,
    padding: '5px 10px',
    border: '1px solid',
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.02)',
  },
};
